import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';

const AdminQuizAddPage = () => {
  const [quiz, setQuiz] = useState({
    title: '',
    courseId: '',
    passingScore: 80,
    questions: [{ questionText: '', options: ['', '', '', ''], correctAnswer: '' }]
  });
  const [courses, setCourses] = useState([]);
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const config = { headers: { 'x-auth-token': token } };
        const res = await axios.get(`${API_BASE_URL}/api/courses`, config);
        setCourses(res.data);
      } catch {
        toast.error('Kurs listesi getirilemedi.');
      }
    };
    if (token) fetchCourses();
  }, [token]);

  const handleQuizInfoChange = (e) => {
    setQuiz({ ...quiz, [e.target.name]: e.target.value });
  };

  const handleQuestionChange = (index, e) => {
    const updated = [...quiz.questions];
    updated[index][e.target.name] = e.target.value;
    setQuiz({ ...quiz, questions: updated });
  };

  const handleCorrectAnswerChange = (qIndex, value) => {
    const updated = [...quiz.questions];
    updated[qIndex].correctAnswer = value;
    setQuiz({ ...quiz, questions: updated });
  };

  const handleOptionChange = (qIndex, oIndex, e) => {
    const updated = [...quiz.questions];
    const value = e.target.value;
    const oldValue = updated[qIndex].options[oIndex];
    updated[qIndex].options[oIndex] = value;
    // Eğer eski doğru cevap buyduysa yeniyle eşitle
    if (updated[qIndex].correctAnswer === oldValue) {
      updated[qIndex].correctAnswer = value;
    }
    setQuiz({ ...quiz, questions: updated });
  };

  const addQuestion = () => {
    setQuiz({
      ...quiz,
      questions: [...quiz.questions, { questionText: '', options: ['', '', '', ''], correctAnswer: '' }]
    });
  };

  const removeQuestion = (index) => {
    if (quiz.questions.length <= 1) return toast.error('Quizde en az bir soru olmalıdır.');
    const updated = quiz.questions.filter((_, i) => i !== index);
    setQuiz({ ...quiz, questions: updated });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Quiz oluşturuluyor...');
    try {
      const config = { headers: { 'x-auth-token': token } };
      const payload = {
        ...quiz,
        questions: quiz.questions.map(q => ({
          ...q,
          options: q.options.filter(opt => opt.trim() !== '')
        }))
      };
      await axios.post(`${API_BASE_URL}/api/quizzes`, payload, config);
      toast.success('Quiz başarıyla oluşturuldu!', { id: loadingToast });
      navigate('/admin/quizzes');
    } catch (err) {
      console.error(err.response);
      toast.error(err.response?.data?.msg || 'Quiz oluşturulamadı.', { id: loadingToast });
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Yeni Quiz Oluştur</h1>
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input type="text" name="title" placeholder="Quiz Başlığı"
            value={quiz.title} onChange={handleQuizInfoChange} required className="input-field md:col-span-2" />
          <input type="number" name="passingScore" placeholder="Geçme Notu (%)"
            value={quiz.passingScore} onChange={handleQuizInfoChange} required className="input-field" />
        </div>

        <select name="courseId" value={quiz.courseId} onChange={handleQuizInfoChange} required className="input-field">
          <option value="">Bir Kurs Seçin</option>
          {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
        </select>

        <div className="space-y-4">
          {quiz.questions.map((q, qIndex) => (
            <div key={qIndex} className="p-4 border rounded-md bg-light-bg dark:bg-dark-bg">
              <div className="flex justify-between items-center mb-2">
                <label className="font-semibold">Soru {qIndex + 1}</label>
                {quiz.questions.length > 1 && (
                  <button type="button" onClick={() => removeQuestion(qIndex)} className="text-red-500 text-sm hover:underline">
                    Soruyu Sil
                  </button>
                )}
              </div>
              <textarea name="questionText" placeholder="Soru metnini yazın..."
                value={q.questionText} onChange={(e) => handleQuestionChange(qIndex, e)}
                required className="input-field"></textarea>

              <p className="text-sm mt-4 mb-2 opacity-70">Seçenekler (doğru olanı işaretleyin):</p>
              {q.options.map((option, oIndex) => (
                <div key={oIndex} className="flex items-center gap-2 mt-2">
                  <input
                    type="radio"
                    name={`correctAnswer-${qIndex}`}
                    value={option}
                    checked={q.correctAnswer === option}
                    onChange={(e) => handleCorrectAnswerChange(qIndex, e.target.value)}
                    required
                  />
                  <input type="text" placeholder={`Seçenek ${oIndex + 1}`}
                    value={option} onChange={(e) => handleOptionChange(qIndex, oIndex, e)}
                    className="input-field" />
                </div>
              ))}
            </div>
          ))}
        </div>

        <button type="button" onClick={addQuestion}
          className="w-full py-2 border-dashed border-2 rounded-md hover:bg-light-bg dark:hover:bg-dark-border transition-colors">
          + Yeni Soru Ekle
        </button>
        <button type="submit" className="btn-primary w-full">Quizi Kaydet</button>
      </form>
    </motion.div>
  );
};

export default AdminQuizAddPage;
